import { BROADCAST_AUDIENCES, BROADCAST_CHANNELS, BROADCAST_TYPES, safeBroadcastUrl, } from "@healthos/shared";
import { z } from "zod";
export const BroadcastType = z.enum(BROADCAST_TYPES);
export const BroadcastAudience = z.enum(BROADCAST_AUDIENCES);
export const BroadcastChannel = z.enum(BROADCAST_CHANNELS);
export const Broadcast = z.object({
    id: z.string(),
    type: BroadcastType,
    title: z.string(),
    body: z.string(),
    contentId: z.string().nullable(),
    contentTitle: z.string().nullable(),
    linkUrl: z.string().nullable(),
    linkLabel: z.string().nullable(),
    audience: BroadcastAudience,
    audienceRef: z.string().nullable(),
    audienceLabel: z.string(),
    companyId: z.string().nullable(),
    showDays: z.number(),
    channels: z.array(BroadcastChannel),
    sendAt: z.string(),
    sentAt: z.string().nullable(),
    recalledAt: z.string().nullable(),
    createdAt: z.string(),
    reached: z.number(),
    opened: z.number(),
    acted: z.number(),
    answered: z.number(),
});
export const BroadcastListResponse = z.object({ broadcasts: z.array(Broadcast) });
const BroadcastMessage = z.object({
    type: BroadcastType,
    title: z.string().trim().min(1).max(80),
    body: z.string().trim().min(1).max(280),
    contentId: z.string().trim().min(1).nullable().default(null),
    linkUrl: z
        .string()
        .trim()
        .max(2000)
        .nullable()
        .default(null)
        .refine((u) => u === null || safeBroadcastUrl(u) !== null, "links must start with https://"),
    linkLabel: z.string().trim().max(24).nullable().default(null),
    showDays: z.number().int().min(1).max(14).default(7),
    channels: z
        .array(BroadcastChannel)
        .min(1, "pick at least one channel")
        .transform((c) => [...new Set(c)]),
});
const namesContent = (b) => (b.type === "article" || b.type === "reset") === Boolean(b.contentId) &&
    (b.linkUrl === null || b.type === "announcement" || b.type === "reminder");
const CONTENT_ISSUE = {
    message: "articles and resets name one library item; announcements and reminders may carry a link",
    path: ["contentId"],
};
export const BroadcastTestRequest = BroadcastMessage.refine(namesContent, CONTENT_ISSUE);
export const BroadcastCreateRequest = BroadcastMessage.extend({
    audience: BroadcastAudience,
    audienceRef: z.guid().nullable().default(null),
    // At least a minute out, so there is always a window to recall it.
    sendAt: z.iso.datetime({ offset: true }),
})
    .refine((b) => b.audience === "all" || b.audience === "company" || b.audienceRef, {
    message: "pick who it goes to",
    path: ["audienceRef"],
})
    .refine(namesContent, CONTENT_ISSUE);
export const BroadcastResponse = z.object({ broadcast: Broadcast });
export const BroadcastTestResponse = z.object({ reached: z.number() });
export const BroadcastIdParams = z.object({ id: z.guid() });
export const BroadcastRecallResponse = z.object({
    status: z.enum(["recalled", "already_recalled"]),
    wasSent: z.boolean(),
    pulled: z.number(),
    pushesStopped: z.number(),
});
export const BroadcastAudienceQuery = z.object({
    companyId: z.string().optional(),
    audience: BroadcastAudience,
    ref: z.guid().optional(),
});
export const BroadcastAudienceResponse = z.object({ count: z.number(), label: z.string() });
export const BroadcastSearchQuery = z.object({
    companyId: z.string().optional(),
    q: z.string().trim().max(80).default(""),
    type: z.enum(["article", "reset"]).optional(),
});
export const BroadcastOption = z.object({
    id: z.string(),
    label: z.string(),
    detail: z.string().nullable(),
    imageUrl: z.string().nullable().optional(),
});
export const BroadcastOptionsResponse = z.object({ options: z.array(BroadcastOption) });
//# sourceMappingURL=broadcasts.js.map